import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Paper
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface RequiredInput {
  identifier: string;
  label?: string;
  description?: string;
  required?: boolean;
  file_types?: string[];
}

// convert snake_case identifier to a readable label
function displayLabel(input: RequiredInput): string {
  if (input.label) return input.label;
  return input.identifier.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface FileInputCategorizerProps {
  files: File[];
  requiredInputs: RequiredInput[];
  onChange: (mapping: Record<string, string>) => void;
  disabled?: boolean;
}

export default function FileInputCategorizer({
  files,
  requiredInputs,
  onChange,
  disabled = false
}: FileInputCategorizerProps) {
  // mapping: fileName -> identifier
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Auto-map on mount based on filename heuristics
  useEffect(() => {
    const autoMapping: Record<string, string> = {};
    const assignedIdentifiers = new Set<string>();

    for (const file of files) {
      const normalized = file.name
        .replace(/^\d{13}_/, '') // strip timestamp prefix
        .replace(/\.[^.]+$/, '') // strip extension
        .toLowerCase()
        .replace(/[\s-]+/g, '_');

      for (const input of requiredInputs) {
        if (assignedIdentifiers.has(input.identifier)) continue;
        if (normalized.includes(input.identifier) || input.identifier.includes(normalized)) {
          autoMapping[file.name] = input.identifier;
          assignedIdentifiers.add(input.identifier);
          break;
        }
      }
    }

    setMapping(autoMapping);
  }, [files, requiredInputs]);

  // Propagate mapping changes to parent
  useEffect(() => {
    onChange(mapping);
  }, [mapping, onChange]);

  const handleChange = useCallback((fileName: string, identifier: string) => {
    setMapping(prev => {
      const next = { ...prev };
      if (identifier === '') {
        delete next[fileName];
      } else {
        // Remove any other file that was assigned this identifier
        for (const [key, val] of Object.entries(next)) {
          if (val === identifier && key !== fileName) {
            delete next[key];
          }
        }
        next[fileName] = identifier;
      }
      return next;
    });
  }, []);

  const assignedIdentifiers = new Set(Object.values(mapping));
  const unmappedRequired = requiredInputs.filter(
    inp => inp.required !== false && !assignedIdentifiers.has(inp.identifier)
  );
  const allRequiredMapped = unmappedRequired.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        Map Files to Document Types
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assign each uploaded file to its document type so the workflow can process them correctly.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {files.map((file) => {
          const ext = file.name.split('.').pop()?.toLowerCase() || '';
          const selectedId = mapping[file.name] || '';
          const isMapped = !!selectedId;

          return (
            <Box
              key={file.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: isMapped ? 'action.selected' : 'background.default',
                border: '1px solid',
                borderColor: isMapped ? 'success.light' : 'divider'
              }}
            >
              <InsertDriveFileIcon color={isMapped ? 'success' : 'action'} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={file.name}>
                  {file.name}
                </Typography>
                <Chip label={ext.toUpperCase()} size="small" sx={{ mt: 0.5 }} />
              </Box>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={selectedId}
                  onChange={(e) => handleChange(file.name, e.target.value as string)}
                  label="Document Type"
                  disabled={disabled}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {requiredInputs.map((input) => {
                    const takenByOther = assignedIdentifiers.has(input.identifier)
                      && mapping[file.name] !== input.identifier;
                    // Check file type compatibility
                    const typeOk = !input.file_types || input.file_types.length === 0
                      || input.file_types.includes(ext);
                    return (
                      <MenuItem
                        key={input.identifier}
                        value={input.identifier}
                        disabled={takenByOther || !typeOk}
                      >
                        {displayLabel(input)}
                        {input.required !== false ? ' *' : ''}
                        {takenByOther ? ' (assigned)' : ''}
                        {!typeOk ? ` (${input.file_types?.join('/')} only)` : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {isMapped && <CheckCircleIcon color="success" fontSize="small" />}
            </Box>
          );
        })}
      </Box>

      {!allRequiredMapped && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Missing required mappings: {unmappedRequired.map(i => displayLabel(i)).join(', ')}
        </Alert>
      )}
      {allRequiredMapped && files.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
          All required documents are mapped.
        </Alert>
      )}
    </Paper>
  );
}
